import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BulkRecurringTransaction,
  RecurrenceType,
  RecurringTransaction,
  SavedAttachment,
  Transaction,
} from '@app/modules/businesses/models/statements.models';
import { StatementsFacade } from '@app/modules/businesses/services/statements.facade';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { dedupeArray } from '@app/shared/utils/lib/general.util';
import { format } from 'date-fns';
import { BehaviorSubject, combineLatest, EMPTY, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import { MyBusinessRoutes } from '../../interfaces/constants';
import { DialogDeleteRecurringTransactionComponent } from '../dialog-delete-recurring-transaction/dialog-delete-recurring-transaction.component';

const recurrenceOptions: { label: string; value: RecurrenceType }[] = [
  { label: 'Never', value: null },
  { label: 'Per Reservation', value: RecurrenceType.PerReservation },
  { label: 'Every Booked Night', value: RecurrenceType.EveryBookedNight },
  { label: 'Weekly', value: RecurrenceType.Weekly },
  { label: 'Monthly', value: RecurrenceType.Monthly },
];

@Component({
  selector: 'sbnb-transaction-form',
  templateUrl: './transaction-form.component.html',
  styles: [
    `
      :host ::ng-deep .currency .mat-form-field-prefix {
        top: 0 !important;
      }

      :host ::ng-deep .datepicker .mat-form-field-suffix {
        top: 6px !important;
        right: -12px !important;
      }

      :host ::ng-deep .mat-form-field-appearance-outline .mat-form-field-outline {
        background-color: white;
        border-radius: 5px;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransactionFormComponent implements OnInit {
  @Input() editing = false;
  @Input() recurring = false;
  @Output() saveSuccess = new EventEmitter<void>();

  private destroyRef = inject(DestroyRef);

  public readonly$ = new BehaviorSubject<boolean>(false);
  public adjustmentUuid$ = new BehaviorSubject<string>(null);
  public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public saving$ = new BehaviorSubject<boolean>(false);
  public transaction$ = new BehaviorSubject<Transaction>(null);
  public transactionForm = this.fb.group({
    date: [new Date().toISOString(), Validators.required],
    recurrence: [null],
    lines: this.fb.array([
      this.fb.group({
        category: ['', Validators.required],
        description: ['', Validators.required],
        amount: [null, Validators.required],
        markup: [null],
        property_id: [null, Validators.required],
      }),
    ]),
  });
  public recurringPropertyIdsCtrl = new FormControl<number[]>([]);

  public attachments: File[] = [];
  public previousAttachments$ = new BehaviorSubject<SavedAttachment[]>(null);

  propertiesState$ = this.statementsFacade.propertiesState$;
  public recurrenceOptions = recurrenceOptions;

  // Switches between lines for a regular transaction and the recurring property ids for a recurring transaction
  // depending on which version of the form is being used.
  private selectedPropertyIds$ = this.recurrence.valueChanges.pipe(
    startWith(null),
    switchMap(() => {
      if (this.recurrence.value) {
        return this.recurringPropertyIdsCtrl.valueChanges;
      }
      return this.lines.valueChanges.pipe(
        startWith([]),
        map((lines) => lines.map((line) => line.property_id).filter((id) => Boolean(id)) as number[])
      );
    }),
    shareReplay(1)
  );

  // All properties in a txn must have the same currency
  currentPropertyCurrency$ = combineLatest([this.propertiesState$, this.selectedPropertyIds$]).pipe(
    map(([propertiesState, propertyIds]) => {
      const properties = propertiesState.data.filter((property) => propertyIds.includes(property.id));
      const uniqueCurrencies = dedupeArray(properties.map((property) => property.currency).filter(Boolean));
      return uniqueCurrencies.length === 1 ? uniqueCurrencies[0] : null;
    }),
    distinctUntilChanged(),
    shareReplay(1) // we subscribe in the template in a bunch of places, should just share this state
  );

  private currentPropertyBusiness$ = combineLatest([this.propertiesState$, this.selectedPropertyIds$]).pipe(
    map(([propertiesState, propertyIds]) => {
      const property = propertiesState.data.find((property) => propertyIds.includes(property.id));
      return property?.business?.uuid ?? null;
    }),
    shareReplay(1)
  );

  propertiesMatchingCurrentProperty$ = combineLatest([
    this.propertiesState$,
    this.currentPropertyCurrency$,
    this.currentPropertyBusiness$,
  ]).pipe(
    filter(([propertiesState]) => !!propertiesState.data.length),

    map(([propertiesState, currency, businessUuid]) => {
      return propertiesState.data.filter((property) => {
        // nothing is selected, show all properties
        if (!currency) {
          return true;
        }
        // if the currency doesn't match, remove from the list
        if (property.currency !== currency) {
          return false;
        }

        // If the property belongs to a business, show only that business's properties
        // and if the property doesn't belong to a business, show only other properties without a business
        return (property.business?.uuid ?? null) === businessUuid;
      });
    }),
    map((properties) =>
      properties.map((property) => ({
        label: property.name,
        value: property.id,
        image: {
          src: property.picture,
          alt: property.name,
          size: 'sm',
        },
      }))
    ),
    shareReplay(1)
  );

  // Based on a property, get whether or not markup is allowed and what the categories are.
  public transactionConfiguration$ = this.selectedPropertyIds$.pipe(
    map((propertyIds) => propertyIds?.sort((a, b) => a - b)?.[0]),
    distinctUntilChanged(),
    switchMap((propertyId) => {
      if (!propertyId) {
        return of(null);
      }
      return this.statementsFacade.getTransactionConfiguration(propertyId);
    }),
    tap((configuration) => {
      if (!configuration) {
        return;
      }

      // If markup is not allowed, disable the markup field
      this.lines.controls.forEach((line) => {
        if (!configuration.with_markup) {
          line.get('markup')?.setValue(null);
          line.get('markup')?.disable();
        } else {
          line.get('markup')?.enable();
        }
      });
    }),
    shareReplay(1)
  );

  public reimbursementSelected$ = this.lines.valueChanges.pipe(
    map((lines) => lines.some((line) => line.category === 'reimbursement')),
    shareReplay(1)
  );

  public transactionRoute = MyBusinessRoutes.Transactions;
  public adjustmentsRoute = MyBusinessRoutes.Adjustments;

  constructor(
    private statementsFacade: StatementsFacade,
    private route: ActivatedRoute,
    private router: Router,
    private fb: UntypedFormBuilder,
    private toast: ToastNotificationsService,
    private dialog: MatDialog,
  ) {
    this.statementsFacade.getProperties();
  }

  ngOnInit() {
    if (this.editing && !this.recurring) {
      this.loading$.next(true);
      this.getTransaction();

      return;
    }

    if (this.editing && this.recurring) {
      this.loading$.next(true);
      this.getRecurringTransaction();
      return;
    }

    // If the user chooses a recurrence type, we switch which version of "property" field is required
    this.recurrence.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (value) {
        this.lines.controls[0].get('property_id')?.disable();
        this.recurringPropertyIdsCtrl.setValidators([Validators.required]);
      } else {
        this.lines.controls[0].get('property_id')?.enable();
        this.recurringPropertyIdsCtrl.clearValidators();
      }
    });

    // Reimbursement is a special case, similar to a "credit". Markup is not allowed and all other
    // lines must also have the reimbursement category.
    this.reimbursementSelected$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (value) {
        this.lines.controls.forEach((line) => {
          line.get('category')?.setValue('reimbursement', { emitEvent: false });
          line.get('markup')?.setValue(null, { emitEvent: false });
          line.get('markup')?.disable({ emitEvent: false });
        });
      } else {
        this.lines.controls.forEach((line) => {
          line.get('markup')?.enable({ emitEvent: false });
        });
      }
    });
  }

  private getTransaction() {
    this.statementsFacade
      .getTransaction(this.transactionId)
      .pipe(
        catchError((error) => {
          if (error.status === 404) {
            this.toast.error('Transaction not found');
            this.router.navigate([this.transactionRoute]);
            return EMPTY;
          }
          this.toast.error('There was an error loading this transaction.');
          return EMPTY;
        })
      )
      .subscribe((transaction) => {
        if (!transaction) {
          return;
        }
        this.transaction$.next(transaction);
        this.hydrateForm(transaction);
        this.loading$.next(false);
      });
  }

  private getRecurringTransaction() {
    this.statementsFacade
      .getRecurringTransaction(this.transactionId)
      .pipe(
        catchError((error) => {
          if (error.status === 404) {
            this.toast.error('Recurring transaction not found');
            this.router.navigate([this.transactionRoute]);
            return EMPTY;
          }
          this.toast.error('There was an error loading this recurring transaction.');
          return EMPTY;
        })
      )
      .subscribe((recurringTransaction) => {
        if (!recurringTransaction) {
          return;
        }
        const { property, ...data } = recurringTransaction;
        data.lines[0].property = property;

        this.transaction$.next(recurringTransaction);
        this.hydrateForm(data);
        this.loading$.next(false);
      });
  }

  get lines() {
    return this.transactionForm.get('lines') as UntypedFormArray;
  }

  get date() {
    return this.transactionForm.get('date') as UntypedFormControl;
  }

  public get recurrence() {
    return this.transactionForm.get('recurrence') as UntypedFormControl;
  }

  private get transactionId() {
    return Number(this.route.snapshot.params['id']);
  }

  addLine() {
    this.lines.push(
      this.fb.group({
        category: [null, Validators.required],
        description: ['', Validators.required],
        amount: [null, Validators.required],
        markup: [null],
        property_id: [null, Validators.required],
      })
    );
    this.negateRecurrence();
  }

  removeLine(index: number) {
    this.lines.removeAt(index);
    this.enableRecurrence();
  }

  saveChanges() {
    this.saving$.next(true);

    if (this.transactionForm.get('recurrence')?.value) {
      this.saveRecurringTransaction();
      return;
    }

    const transaction = {
      ...this.transactionForm.value,
      attachments: this.attachments,
    };
    delete transaction.recurrence;
    if (this.editing) {
      this.statementsFacade
        .updateTransaction(this.transactionId, transaction as unknown as Transaction)
        .subscribe(this.handleSave());
      return;
    }

    this.statementsFacade.addTransaction(transaction as unknown as Transaction).subscribe(this.handleSave());
  }

  public deleteTransaction() {
    if (!this.editing) {
      return;
    }
    if (!this.recurring) {
      this.statementsFacade.deleteTransaction(this.transactionId).subscribe(this.handleDelete('transaction'));

      return;
    }

    const transaction = this.transaction$.value as RecurringTransaction;
    
    if (transaction.deletable_transactions_count === 0) {
      this.statementsFacade
        .deleteRecurringTransaction(this.transactionId)
        .subscribe(this.handleDelete('recurring transaction'));

      return;
    }
    
    const dialogRef = this.dialog.open(DialogDeleteRecurringTransactionComponent, {
      width: '500px',
      data: {
        deletableTransactionsCount: transaction?.deletable_transactions_count || 0,
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.confirmed) {
        this.statementsFacade
          .deleteRecurringTransaction(this.transactionId, result.deleteRelated)
          .subscribe(this.handleDelete('recurring transaction'));
      }
    });
  }

  private handleDelete(type: string) {
    return {
      next: () => {
        this.router.navigate([this.transactionRoute]);
        this.toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} deleted!`);
      },
      error: () => {
        this.toast.error(`Oops! There was an error deleting this ${type}.`);
      },
    };
  }

  private saveRecurringTransaction() {
    if (this.editing) {
      const recurringTransaction = {
        ...this.transactionForm.value,
        property_id: this.lines.controls[0].get('property_id')?.value,
        recurrence: {
          type: this.transactionForm.get('recurrence')?.value,
          date: format(this.date.value, 'YYYY-MM-DD'),
        },
      };
      delete recurringTransaction.date;
      this.statementsFacade
        .updateRecurringTransaction(this.transactionId, recurringTransaction as unknown as RecurringTransaction)
        .subscribe(this.handleSave());
      return;
    }

    const lines = this.recurringPropertyIdsCtrl.value.map((propertyId) => ({
      ...this.transactionForm.value.lines[0],
      property_id: propertyId,
    }));
    const bulkRecurringTransaction = {
      ...this.transactionForm.value,
      lines,
      recurrence: {
        type: this.transactionForm.get('recurrence')?.value,
        date: format(this.date.value, 'YYYY-MM-DD'),
      },
    };
    this.statementsFacade
      .bulkAddRecurringTransaction(bulkRecurringTransaction as unknown as BulkRecurringTransaction)
      .subscribe(this.handleSave());
  }

  private handleSave() {
    return {
      next: () => {
        this.saving$.next(false);
        this.saveSuccess.emit();
      },
      error: () => {
        this.saving$.next(false);
      },
    };
  }

  public filesSelected(files) {
    this.attachments = files;
    this.negateRecurrence();
  }

  public fileRemoved(id: number) {
    if (!this.editing) {
      this.attachments = [];
      this.enableRecurrence();
      return;
    }

    const attachment = this.previousAttachments$.value.find((attachment) => attachment.id === id);
    this.statementsFacade.deleteAttachment(Number(attachment.id)).subscribe({
      next: () => {
        this.toast.success('Attachment deleted');
      },
      error: (err) => {
        if (err) {
          this.toast.error('Error deleting attachment');
        }
      },
    });
  }

  public get recurrenceTooltip() {
    if (!this.recurrence.disabled) return null;

    if (this.recurring) {
      return 'The schedule for a recurring transaction cannot be edited.';
    }

    return 'This transaction cannot be recurring. Try removing lines or attachments';
  }

  private hydrateForm(transaction: Transaction | RecurringTransaction) {
    const { attachments, lines, recurrence, ...transactionData } = transaction as any;

    if (recurrence) {
      (transactionData as any).recurrence = recurrence.type;
    }
    this.transactionForm.patchValue(transactionData);
    this.lines.clear();
    lines.forEach((line) => {
      line.property_id = line.property?.id;
      this.lines.push(this.fb.group(line));
    });

    this.previousAttachments$.next(attachments as SavedAttachment[]);

    if (transaction.locked || transaction.adjustment_uuid) {
      this.transactionForm.disable();
      this.readonly$.next(true);
      this.adjustmentUuid$.next(transaction.adjustment_uuid);
    }

    if (this.recurring && this.editing) {
      this.recurrence.disable();
      this.date.disable();
    }
  }

  private negateRecurrence() {
    this.recurrence.setValue(null);
    this.recurrence.disable();
  }

  private enableRecurrence() {
    if (this.attachments.length || this.lines.length > 1) {
      return;
    }
    this.recurrence.enable();
  }
}
